import { useState, useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────
// Add to public/index.html <head>:
//   <script src="https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/dist/face-api.esm.js"></script>
// ─────────────────────────────────────────────────────────────────────────────
const MODELS_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/model';

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
  violet:  '#7c3aed',
  purple:  '#a855f7',
  fuchsia: '#d946ef',
  cyan:    '#06b6d4',
  lime:    '#84cc16',
  amber:   '#f59e0b',
  rose:    '#f43f5e',
  white:   '#ffffff',
};

// ── Filter catalogue ─────────────────────────────────────────────────────────
const AR_FILTERS = [
  { id: 'normal',     name: 'Raw',        tag: 'LIVE',    desc: 'No overlay'        },
  { id: 'neonmask',   name: 'Aura',       tag: 'AR',      desc: 'Biometric outline' },
  { id: 'neural',     name: 'Synapse',    tag: 'AR',      desc: 'Neural lattice'    },
  { id: 'cybernetic', name: 'Grid',       tag: 'AR',      desc: 'Scan lines'        },
  { id: 'hologram',   name: 'Hologram',   tag: 'NEW',     desc: 'RGB depth shift'   },
  { id: 'crown',      name: 'Crown',      tag: 'AR',      desc: 'Royal headpiece'   },
  { id: 'sunglasses', name: 'Shades',     tag: 'AR',      desc: 'Eye tracking'      },
  { id: 'particles',  name: 'Orbit',      tag: 'NEW',     desc: 'Face particles'    },
];

const STYLE_FILTERS = [
  { id: 'none',      name: 'Clean',    tag: 'LIVE', desc: 'Original feed'       },
  { id: 'glitch',    name: 'Glitch',   tag: 'FX',   desc: 'Chromatic tear'      },
  { id: 'neon',      name: 'Neon',     tag: 'FX',   desc: 'Deep violet push'    },
  { id: 'thermal',   name: 'Thermal',  tag: 'FX',   desc: 'Infrared palette'    },
  { id: 'noir',      name: 'Noir',     tag: 'NEW',  desc: 'High-contrast mono'  },
  { id: 'duotone',   name: 'Duotone',  tag: 'NEW',  desc: 'Fuchsia / cyan split'},
];

// ── Particle pool (reused across frames) ─────────────────────────────────────
const PARTICLE_COUNT = 60;
const particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
  angle: (i / PARTICLE_COUNT) * Math.PI * 2,
  radius: 0.8 + Math.random() * 0.6,
  speed: 0.004 + Math.random() * 0.006,
  size: 1.5 + Math.random() * 2.5,
  alpha: 0.4 + Math.random() * 0.6,
  trail: [],
}));

export default function AIStudio() {
  const [showLanding, setShowLanding]       = useState(true);
  const [activeTab, setActiveTab]           = useState('faceAR');
  const [currentAR, setCurrentAR]           = useState('normal');
  const [currentStyle, setCurrentStyle]     = useState('none');
  const [captures, setCaptures]             = useState([]);
  const [modelsReady, setModelsReady]       = useState(false);
  const [faceDetected, setFaceDetected]     = useState(false);
  const [trackingStatus, setTrackingStatus] = useState('Loading…');

  const videoRef        = useRef(null);
  const canvasRef       = useRef(null);
  const rafRef          = useRef(null);
  const currentARRef    = useRef('normal');
  const currentStyleRef = useRef('none');
  const lastFaceRef     = useRef(null);
  const frameRef        = useRef(0);

  // ── Load models ────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const faceapi = window.faceapi;
        if (!faceapi) { setTrackingStatus('CDN missing'); return; }
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URL),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODELS_URL),
        ]);
        setModelsReady(true);
        setTrackingStatus('Tracking');
      } catch (e) {
        setTrackingStatus('Error');
      }
    };
    load();
  }, []);

  const startStudio = () => { setShowLanding(false); setTimeout(startCamera, 500); };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        videoRef.current.onloadeddata = () => renderLoop();
      }
    } catch { alert('Camera access denied.'); }
  };

  const detectFace = async (video) => {
    const faceapi = window.faceapi;
    if (!faceapi || !modelsReady) return null;
    try {
      return await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.38 }))
        .withFaceLandmarks(true) || null;
    } catch { return null; }
  };

  // ── Render loop ────────────────────────────────────────────────────────────
  const renderLoop = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.paused || video.ended) {
      rafRef.current = requestAnimationFrame(renderLoop);
      return;
    }
    frameRef.current++;
    const ctx = canvas.getContext('2d');
    if (canvas.width !== video.videoWidth) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    const W = canvas.width, H = canvas.height;

    // Mirror draw
    ctx.save(); ctx.scale(-1, 1);
    ctx.drawImage(video, -W, 0, W, H);
    ctx.restore();

    applyStyleFilter(ctx, W, H);

    // Detect every 3 frames
    if (modelsReady && currentARRef.current !== 'normal' && frameRef.current % 3 === 0) {
      const det = await detectFace(video);
      if (det) { lastFaceRef.current = det; setFaceDetected(true); }
      else setFaceDetected(false);
    }

    applyARFilter(ctx, W, H, lastFaceRef.current);
    rafRef.current = requestAnimationFrame(renderLoop);
  };

  // ── Style filters ──────────────────────────────────────────────────────────
  const applyStyleFilter = (ctx, W, H) => {
    const style = currentStyleRef.current;
    if (style === 'none') return;

    const imageData = ctx.getImageData(0, 0, W, H);
    const d = imageData.data;
    const t = Date.now();

    if (style === 'glitch') {
      // Multi-channel chromatic aberration
      const r_shift = Math.floor(Math.sin(t * 0.002) * 12 + 4);
      const b_shift = -Math.floor(Math.cos(t * 0.0015) * 10 + 3);
      const tmp = new Uint8ClampedArray(d);
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const i = (y * W + x) * 4;
          const rX = Math.min(W - 1, Math.max(0, x + r_shift));
          const bX = Math.min(W - 1, Math.max(0, x + b_shift));
          const rI = (y * W + rX) * 4;
          const bI = (y * W + bX) * 4;
          d[i]     = tmp[rI];       // R shifted right
          d[i + 1] = tmp[i + 1];   // G unchanged
          d[i + 2] = tmp[bI + 2];  // B shifted left
        }
      }
      ctx.putImageData(imageData, 0, 0);
      // Scanline tears
      if (Math.random() < 0.15) {
        const tearY = Math.random() * H;
        const tearH = Math.random() * 6 + 1;
        ctx.fillStyle = `rgba(255,0,${Math.floor(Math.random()*80)},0.55)`;
        ctx.fillRect(0, tearY, W, tearH);
      }
      if (Math.random() < 0.08) {
        const blockY = Math.floor(Math.random() * H / 8) * 8;
        const blockH = Math.floor(Math.random() * 5 + 1) * 8;
        const shift2 = (Math.random() - 0.5) * 40;
        ctx.drawImage(ctx.canvas, 0, blockY, W, blockH, shift2, blockY, W, blockH);
      }
    }
    else if (style === 'neon') {
      for (let i = 0; i < d.length; i += 4) {
        const lum = d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114;
        // Violet push with preserved highlights
        d[i]     = Math.min(255, lum * 0.18 + d[i+2] * 0.5);
        d[i + 1] = Math.min(255, lum * 0.04);
        d[i + 2] = Math.min(255, lum * 1.8 + 30);
      }
      ctx.putImageData(imageData, 0, 0);
      // Vignette
      const vg = ctx.createRadialGradient(W/2, H/2, H*0.3, W/2, H/2, H*0.85);
      vg.addColorStop(0, 'rgba(50,0,100,0)');
      vg.addColorStop(1, 'rgba(10,0,40,0.7)');
      ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H);
      // Scanlines
      ctx.fillStyle = 'rgba(0,0,0,0.12)';
      for (let y = 0; y < H; y += 3) { ctx.fillRect(0, y, W, 1); }
    }
    else if (style === 'thermal') {
      for (let i = 0; i < d.length; i += 4) {
        const heat = (d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114) / 255;
        // Blue → teal → yellow → red/white
        if (heat < 0.25) {
          d[i] = 0; d[i+1] = 0; d[i+2] = Math.min(255, heat * 4 * 200 + 55);
        } else if (heat < 0.5) {
          const f = (heat - 0.25) * 4;
          d[i] = 0; d[i+1] = Math.min(255, f * 200); d[i+2] = Math.min(255, 200 - f * 200);
        } else if (heat < 0.75) {
          const f = (heat - 0.5) * 4;
          d[i] = Math.min(255, f * 255); d[i+1] = Math.min(255, 200 - f * 80); d[i+2] = 0;
        } else {
          const f = (heat - 0.75) * 4;
          d[i] = 255; d[i+1] = Math.min(255, 120 + f * 135); d[i+2] = Math.min(255, f * 255);
        }
      }
      ctx.putImageData(imageData, 0, 0);
    }
    else if (style === 'noir') {
      for (let i = 0; i < d.length; i += 4) {
        const lum = d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114;
        // High contrast S-curve
        const curved = lum < 128
          ? Math.pow(lum / 128, 0.6) * 128
          : 128 + Math.pow((lum - 128) / 128, 1.4) * 128;
        d[i] = d[i+1] = d[i+2] = Math.min(255, curved);
      }
      ctx.putImageData(imageData, 0, 0);
      // Grain
      ctx.save(); ctx.globalAlpha = 0.06;
      for (let gx = 0; gx < W; gx += 2) {
        for (let gy = 0; gy < H; gy += 2) {
          if (Math.random() > 0.5) { ctx.fillStyle = '#fff'; ctx.fillRect(gx, gy, 1, 1); }
        }
      }
      ctx.restore();
      // Vignette
      const vg2 = ctx.createRadialGradient(W/2, H/2, H*0.25, W/2, H/2, H*0.82);
      vg2.addColorStop(0, 'rgba(0,0,0,0)');
      vg2.addColorStop(1, 'rgba(0,0,0,0.75)');
      ctx.fillStyle = vg2; ctx.fillRect(0, 0, W, H);
    }
    else if (style === 'duotone') {
      // Fuchsia shadows → cyan highlights
      for (let i = 0; i < d.length; i += 4) {
        const lum = (d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114) / 255;
        // Shadow color: fuchsia (217, 70, 239)
        // Highlight color: cyan (6, 182, 212)
        d[i]     = Math.round(217 * (1 - lum) + 6   * lum);
        d[i + 1] = Math.round( 70 * (1 - lum) + 182 * lum);
        d[i + 2] = Math.round(239 * (1 - lum) + 212 * lum);
      }
      ctx.putImageData(imageData, 0, 0);
    }
  };

  // ── AR filters ─────────────────────────────────────────────────────────────
  const applyARFilter = (ctx, W, H, detection) => {
    if (currentARRef.current === 'normal') return;
    const t = Date.now();
    const f = frameRef.current;

    let cx, cy, fs, lm;
    if (detection) {
      const box = detection.detection.box;
      cx = W - (box.x + box.width  / 2);
      cy =      box.y + box.height / 2;
      fs = box.width / 2;
      lm = detection.landmarks.positions.map(p => ({ x: W - p.x, y: p.y }));
    } else {
      cx = W / 2; cy = H / 2; fs = W * 0.28; lm = null;
    }

    const avg = (idxs) => {
      if (!lm) return { x: cx, y: cy };
      const pts = idxs.map(i => lm[i]);
      return { x: pts.reduce((s,p)=>s+p.x,0)/pts.length, y: pts.reduce((s,p)=>s+p.y,0)/pts.length };
    };

    ctx.save();

    // ── AURA (neonmask) ─────────────────────────────────────────────────────
    if (currentARRef.current === 'neonmask') {
      const pulse = 0.92 + Math.sin(t * 0.003) * 0.08;

      // Outer soft glow ring
      const outerGlow = ctx.createRadialGradient(cx, cy, fs * 0.9 * pulse, cx, cy, fs * 1.5 * pulse);
      outerGlow.addColorStop(0, 'rgba(168,85,247,0.18)');
      outerGlow.addColorStop(0.5, 'rgba(168,85,247,0.06)');
      outerGlow.addColorStop(1, 'rgba(168,85,247,0)');
      ctx.fillStyle = outerGlow;
      ctx.beginPath(); ctx.arc(cx, cy, fs * 1.5 * pulse, 0, Math.PI * 2); ctx.fill();

      // Main circle — dashed arc
      ctx.strokeStyle = `rgba(192,132,252,${0.7 + Math.sin(t*0.004)*0.3})`;
      ctx.lineWidth = 2.5;
      ctx.shadowColor = '#c084fc'; ctx.shadowBlur = 18;
      ctx.setLineDash([12, 6]);
      ctx.lineDashOffset = -(t * 0.04) % 18;
      ctx.beginPath(); ctx.arc(cx, cy, fs * pulse, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);

      // Inner ring
      ctx.strokeStyle = 'rgba(217,70,239,0.4)';
      ctx.lineWidth = 1;
      ctx.shadowBlur = 6;
      ctx.beginPath(); ctx.arc(cx, cy, fs * 0.6 * pulse, 0, Math.PI * 2); ctx.stroke();

      // Biometric corner brackets
      const bx = cx - fs * 0.9, by = cy - fs * 1.1, bw = fs * 1.8, bh = fs * 2.2;
      const bl = fs * 0.22;
      ctx.strokeStyle = '#a855f7'; ctx.lineWidth = 2; ctx.shadowBlur = 10;
      [
        [[bx,by+bl],[bx,by],[bx+bl,by]],
        [[bx+bw-bl,by],[bx+bw,by],[bx+bw,by+bl]],
        [[bx,by+bh-bl],[bx,by+bh],[bx+bl,by+bh]],
        [[bx+bw,by+bh-bl],[bx+bw,by+bh],[bx+bw-bl,by+bh]],
      ].forEach(pts => {
        ctx.beginPath();
        ctx.moveTo(...pts[0]); ctx.lineTo(...pts[1]); ctx.lineTo(...pts[2]);
        ctx.stroke();
      });

      // Jaw trace
      if (lm) {
        ctx.strokeStyle = 'rgba(192,132,252,0.35)';
        ctx.lineWidth = 1.5; ctx.shadowBlur = 8;
        ctx.beginPath();
        for (let i = 0; i <= 16; i++) {
          i === 0 ? ctx.moveTo(lm[i].x, lm[i].y) : ctx.lineTo(lm[i].x, lm[i].y);
        }
        ctx.stroke();
      }

      // HUD label
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(168,85,247,0.85)';
      ctx.font = '500 9px monospace';
      ctx.fillText('BIOMETRIC · ACTIVE', cx - 44, by - 8);
    }

    // ── SYNAPSE (neural) ────────────────────────────────────────────────────
    else if (currentARRef.current === 'neural') {
      const keyIdxs = [0,2,4,6,8,10,12,14,16,17,19,21,22,24,26,27,30,33,36,39,42,45,48,51,54,57,8];
      const nodes = lm
        ? keyIdxs.map(i => lm[Math.min(i, lm.length-1)])
        : Array.from({length:24}, (_,i) => ({
            x: cx + Math.cos(i/24*Math.PI*2) * fs * (0.5 + Math.random()*0.8),
            y: cy + Math.sin(i/24*Math.PI*2) * fs * (0.5 + Math.random()*0.8),
          }));

      // Draw edges with distance-based opacity
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          const maxD = fs * 1.4;
          if (dist < maxD) {
            const alpha = (1 - dist/maxD) * 0.55;
            ctx.strokeStyle = `rgba(168,85,247,${alpha})`;
            ctx.lineWidth = (1 - dist/maxD) * 1.5;
            ctx.shadowColor = '#a855f7'; ctx.shadowBlur = 4;
            ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y); ctx.stroke();
          }
        }
      }

      // Animated pulse traveling along edges
      const pulseProgress = ((t * 0.0008) % 1);
      if (nodes.length > 1) {
        const srcIdx = Math.floor(f / 20) % nodes.length;
        const dstIdx = (srcIdx + 1) % nodes.length;
        const px = nodes[srcIdx].x + (nodes[dstIdx].x - nodes[srcIdx].x) * pulseProgress;
        const py = nodes[srcIdx].y + (nodes[dstIdx].y - nodes[srcIdx].y) * pulseProgress;
        const pGlow = ctx.createRadialGradient(px, py, 0, px, py, 12);
        pGlow.addColorStop(0, 'rgba(217,70,239,0.9)');
        pGlow.addColorStop(1, 'rgba(217,70,239,0)');
        ctx.fillStyle = pGlow;
        ctx.beginPath(); ctx.arc(px, py, 12, 0, Math.PI*2); ctx.fill();
      }

      // Node dots
      nodes.forEach((n, i) => {
        const blink = Math.sin(t * 0.002 + i * 0.5) > 0.3;
        ctx.fillStyle = blink ? '#d946ef' : 'rgba(168,85,247,0.6)';
        ctx.shadowColor = '#d946ef'; ctx.shadowBlur = blink ? 12 : 4;
        ctx.beginPath(); ctx.arc(n.x, n.y, blink ? 3.5 : 2, 0, Math.PI*2); ctx.fill();
      });
    }

    // ── GRID (cybernetic) ───────────────────────────────────────────────────
    else if (currentARRef.current === 'cybernetic') {
      const top    = cy - fs * 1.15;
      const bottom = cy + fs * 1.15;
      const left   = cx - fs;
      const right  = cx + fs;
      const scanY  = top + ((t * 0.08) % (bottom - top));

      // Grid lines
      const cols = 8, rows = 10;
      ctx.lineWidth = 0.8;
      for (let i = 0; i <= cols; i++) {
        const x = left + (right - left) * (i / cols);
        const dist = Math.abs(x - cx) / fs;
        ctx.strokeStyle = `rgba(6,182,212,${0.15 + (1-dist)*0.25})`;
        ctx.shadowColor = '#06b6d4'; ctx.shadowBlur = 3;
        ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, bottom); ctx.stroke();
      }
      for (let i = 0; i <= rows; i++) {
        const y = top + (bottom - top) * (i / rows);
        const dist = Math.abs(y - cy) / fs;
        ctx.strokeStyle = `rgba(6,182,212,${0.15 + (1-dist)*0.2})`;
        ctx.beginPath(); ctx.moveTo(left, y); ctx.lineTo(right, y); ctx.stroke();
      }

      // Animated scan line
      const scanAlpha = 0.5 + Math.sin(t * 0.005) * 0.3;
      const scanGrad = ctx.createLinearGradient(left, scanY - 8, left, scanY + 8);
      scanGrad.addColorStop(0, 'rgba(6,182,212,0)');
      scanGrad.addColorStop(0.5, `rgba(6,182,212,${scanAlpha})`);
      scanGrad.addColorStop(1, 'rgba(6,182,212,0)');
      ctx.fillStyle = scanGrad; ctx.fillRect(left, scanY - 8, right - left, 16);

      // Border
      ctx.strokeStyle = `rgba(6,182,212,0.8)`;
      ctx.lineWidth = 1.5; ctx.shadowBlur = 12;
      ctx.strokeRect(left, top, right - left, bottom - top);

      // Corner ticks
      const tl = fs * 0.15;
      ctx.lineWidth = 2; ctx.strokeStyle = '#06b6d4';
      [[left,top],[right,top],[left,bottom],[right,bottom]].forEach(([x,y]) => {
        ctx.beginPath();
        ctx.moveTo(x + (x===left?tl:-tl), y); ctx.lineTo(x, y); ctx.lineTo(x, y + (y===top?tl:-tl));
        ctx.stroke();
      });

      // HUD readout
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(6,182,212,0.75)'; ctx.font = '500 8px monospace';
      ctx.fillText(`SCAN ${Math.floor(((scanY-top)/(bottom-top))*100).toString().padStart(3,'0')}%`, left + 4, top - 6);
    }

    // ── HOLOGRAM ─────────────────────────────────────────────────────────────
    else if (currentARRef.current === 'hologram') {
      // RGB channel shift to create depth illusion
      const shiftX = Math.sin(t * 0.002) * 6;
      const shiftY = Math.cos(t * 0.0015) * 2;

      // Snapshot current canvas region
      const rx = Math.max(0, cx - fs * 1.2), ry = Math.max(0, cy - fs * 1.4);
      const rw = fs * 2.4, rh = fs * 2.8;

      ctx.save();
      ctx.globalCompositeOperation = 'screen';

      // Red channel ghost
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = 'rgba(255,0,80,0.4)';
      ctx.drawImage(canvasRef.current, rx, ry, rw, rh, rx + shiftX, ry + shiftY, rw, rh);

      // Blue channel ghost
      ctx.globalAlpha = 0.35;
      ctx.drawImage(canvasRef.current, rx, ry, rw, rh, rx - shiftX, ry - shiftY, rw, rh);

      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.restore();

      // Horizontal scanlines over face area
      ctx.save();
      for (let sy = ry; sy < ry + rh; sy += 3) {
        const alpha = 0.04 + Math.sin((sy + t * 0.05) * 0.2) * 0.02;
        ctx.fillStyle = `rgba(120,200,255,${alpha})`;
        ctx.fillRect(rx, sy, rw, 1);
      }
      ctx.restore();

      // Flicker overlay
      if (Math.random() < 0.06) {
        ctx.save();
        ctx.globalAlpha = 0.08 + Math.random() * 0.12;
        ctx.fillStyle = '#00f0ff';
        ctx.fillRect(rx, ry, rw, rh);
        ctx.restore();
      }

      // Outer glow frame
      ctx.strokeStyle = 'rgba(0,240,255,0.5)';
      ctx.lineWidth = 1;
      ctx.shadowColor = '#00f0ff'; ctx.shadowBlur = 20;
      ctx.strokeRect(rx, ry, rw, rh);

      // Label
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(0,240,255,0.7)'; ctx.font = '500 8px monospace';
      ctx.fillText('HOLOGRAPHIC MODE', rx + 4, ry - 5);
    }

    // ── CROWN ────────────────────────────────────────────────────────────────
    else if (currentARRef.current === 'crown') {
      const browIdxs = [17,18,19,20,21,22,23,24,25,26];
      const topBrowY = lm
        ? Math.min(...browIdxs.map(i => lm[i].y)) - fs * 0.12
        : cy - fs - 20;

      const crownBase = topBrowY - 8;
      const crownH    = fs * 0.7;
      const crownW    = fs * 1.8;
      const pulse     = 1 + Math.sin(t * 0.003) * 0.03;

      // Glow aura above head
      const crownGlow = ctx.createRadialGradient(cx, crownBase - crownH * 0.5, 0, cx, crownBase, crownW * 0.8);
      crownGlow.addColorStop(0, 'rgba(251,191,36,0.25)');
      crownGlow.addColorStop(1, 'rgba(251,191,36,0)');
      ctx.fillStyle = crownGlow;
      ctx.beginPath(); ctx.ellipse(cx, crownBase - crownH*0.3, crownW*0.9, crownH, 0, 0, Math.PI*2); ctx.fill();

      // Crown fill
      const crownGrad = ctx.createLinearGradient(cx, crownBase - crownH, cx, crownBase);
      crownGrad.addColorStop(0, 'rgba(253,224,71,0.85)');
      crownGrad.addColorStop(0.5, 'rgba(251,191,36,0.7)');
      crownGrad.addColorStop(1, 'rgba(180,120,0,0.5)');
      ctx.fillStyle = crownGrad;
      ctx.shadowColor = '#fbbf24'; ctx.shadowBlur = 22;
      ctx.save(); ctx.scale(pulse, pulse); ctx.translate(cx*(1-pulse), crownBase*(1-pulse));

      ctx.beginPath();
      const cL = cx - crownW/2, cR = cx + crownW/2;
      ctx.moveTo(cL, crownBase);
      ctx.lineTo(cL, crownBase - crownH * 0.55);
      ctx.lineTo(cL + crownW*0.18, crownBase - crownH * 0.3);
      ctx.lineTo(cL + crownW*0.34, crownBase - crownH);
      ctx.lineTo(cL + crownW*0.5,  crownBase - crownH * 0.35);
      ctx.lineTo(cx,               crownBase - crownH * 1.1);
      ctx.lineTo(cL + crownW*0.5 + crownW*0.16, crownBase - crownH * 0.35);
      ctx.lineTo(cL + crownW*0.66, crownBase - crownH);
      ctx.lineTo(cR - crownW*0.18, crownBase - crownH * 0.3);
      ctx.lineTo(cR, crownBase - crownH * 0.55);
      ctx.lineTo(cR, crownBase);
      ctx.closePath();
      ctx.fill();

      // Crown stroke
      ctx.strokeStyle = 'rgba(253,224,71,0.9)';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();

      // Gem dots
      [[cL + crownW*0.34, crownBase - crownH],[cx, crownBase - crownH * 1.1],[cL + crownW*0.66, crownBase - crownH]].forEach(([gx,gy],i) => {
        const gemColors = ['#f43f5e','#a855f7','#06b6d4'];
        ctx.fillStyle = gemColors[i]; ctx.shadowColor = gemColors[i]; ctx.shadowBlur = 14;
        ctx.beginPath(); ctx.arc(gx, gy, 4, 0, Math.PI*2); ctx.fill();
      });
    }

    // ── SHADES (sunglasses) ─────────────────────────────────────────────────
    else if (currentARRef.current === 'sunglasses') {
      const leftEye  = avg([36,37,38,39,40,41]);
      const rightEye = avg([42,43,44,45,46,47]);
      const eyeY     = leftEye.y;
      const lx       = leftEye.x,  rx = rightEye.x;
      const gW       = fs * 0.62, gH = fs * 0.36;
      const bridgeY  = eyeY;

      // Lens gradient fill
      const lensGrad = ctx.createLinearGradient(0, eyeY - gH/2, 0, eyeY + gH/2);
      lensGrad.addColorStop(0, 'rgba(168,85,247,0.72)');
      lensGrad.addColorStop(1, 'rgba(109,40,217,0.45)');

      ctx.shadowColor = '#a855f7'; ctx.shadowBlur = 20;

      // Left lens (rounded rect)
      const lLx = lx - gW/2, lLy = eyeY - gH/2;
      ctx.fillStyle = lensGrad;
      ctx.beginPath(); ctx.roundRect(lLx, lLy, gW, gH, gH*0.3); ctx.fill();
      ctx.strokeStyle = 'rgba(192,132,252,0.9)'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(lLx, lLy, gW, gH, gH*0.3); ctx.stroke();

      // Right lens
      const rLx = rx - gW/2;
      ctx.fillStyle = lensGrad;
      ctx.beginPath(); ctx.roundRect(rLx, lLy, gW, gH, gH*0.3); ctx.fill();
      ctx.strokeStyle = 'rgba(192,132,252,0.9)';
      ctx.beginPath(); ctx.roundRect(rLx, lLy, gW, gH, gH*0.3); ctx.stroke();

      // Bridge
      ctx.strokeStyle = 'rgba(192,132,252,0.8)'; ctx.lineWidth = 2; ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.moveTo(lLx + gW + 2, bridgeY);
      ctx.quadraticCurveTo((lx + rx)/2, bridgeY - gH*0.15, rLx - 2, bridgeY);
      ctx.stroke();

      // Temples (arms)
      if (lm) {
        const leftTemple  = lm[0];
        const rightTemple = lm[16];
        ctx.strokeStyle = 'rgba(139,92,246,0.6)'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(lLx, bridgeY); ctx.lineTo(leftTemple.x,  leftTemple.y);  ctx.stroke();
        ctx.beginPath(); ctx.moveTo(rLx + gW, bridgeY); ctx.lineTo(rightTemple.x, rightTemple.y); ctx.stroke();
      }

      // Lens sheen
      ctx.save(); ctx.globalAlpha = 0.25;
      const sheenGrad = ctx.createLinearGradient(lLx, lLy, lLx + gW * 0.5, lLy + gH * 0.5);
      sheenGrad.addColorStop(0, '#fff'); sheenGrad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = sheenGrad;
      ctx.beginPath(); ctx.roundRect(lLx, lLy, gW, gH, gH*0.3); ctx.fill();
      ctx.beginPath(); ctx.roundRect(rLx, lLy, gW, gH, gH*0.3); ctx.fill();
      ctx.restore();
    }

    // ── ORBIT (particles) ───────────────────────────────────────────────────
    else if (currentARRef.current === 'particles') {
      particles.forEach(p => {
        p.angle += p.speed;
        const orbitX = cx + Math.cos(p.angle) * fs * p.radius;
        const orbitY = cy + Math.sin(p.angle * 0.7) * fs * p.radius * 0.55; // elliptical

        p.trail.push({ x: orbitX, y: orbitY });
        if (p.trail.length > 12) p.trail.shift();

        // Trail
        if (p.trail.length > 1) {
          for (let i = 1; i < p.trail.length; i++) {
            const trailAlpha = (i / p.trail.length) * p.alpha * 0.4;
            ctx.strokeStyle = `rgba(217,70,239,${trailAlpha})`;
            ctx.lineWidth = p.size * (i / p.trail.length);
            ctx.shadowColor = '#d946ef'; ctx.shadowBlur = 6;
            ctx.beginPath();
            ctx.moveTo(p.trail[i-1].x, p.trail[i-1].y);
            ctx.lineTo(p.trail[i].x,   p.trail[i].y);
            ctx.stroke();
          }
        }

        // Head
        const hGlow = ctx.createRadialGradient(orbitX, orbitY, 0, orbitX, orbitY, p.size * 3);
        hGlow.addColorStop(0, `rgba(217,70,239,${p.alpha})`);
        hGlow.addColorStop(1, 'rgba(217,70,239,0)');
        ctx.fillStyle = hGlow;
        ctx.beginPath(); ctx.arc(orbitX, orbitY, p.size * 3, 0, Math.PI*2); ctx.fill();

        ctx.fillStyle = '#fff'; ctx.shadowBlur = 0;
        ctx.beginPath(); ctx.arc(orbitX, orbitY, p.size * 0.5, 0, Math.PI*2); ctx.fill();
      });

      // Orbit ring guide
      ctx.strokeStyle = 'rgba(217,70,239,0.12)';
      ctx.lineWidth = 1;
      ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.ellipse(cx, cy, fs * 1.2, fs * 0.66, 0, 0, Math.PI*2); ctx.stroke();
    }

    ctx.restore();
  };

  // ── Capture ────────────────────────────────────────────────────────────────
  const captureImage = async () => {
    if (!canvasRef.current) return;
    const dataUrl    = canvasRef.current.toDataURL('image/png');
    const filterType = currentARRef.current !== 'normal' ? currentARRef.current : currentStyleRef.current;
    setCaptures(prev => [{ url: dataUrl, timestamp: Date.now(), filter: filterType }, ...prev]);
    try { await axios.post(`${API}/captures`, { image_data: dataUrl, filter_type: filterType }); }
    catch (e) { console.error(e); }
  };

  const downloadCapture = (url) => {
    const a = document.createElement('a'); a.href = url;
    a.download = `mbk-studio-${Date.now()}.png`; a.click();
  };

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  // ── TAG badge colors ───────────────────────────────────────────────────────
  const tagColor = (tag) => ({
    'LIVE': 'bg-green-500/15 text-green-400 border-green-500/20',
    'AR':   'bg-purple-500/15 text-purple-400 border-purple-500/20',
    'FX':   'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/20',
    'NEW':  'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  }[tag] || 'bg-gray-500/15 text-gray-400');

  // ── Studio UI ──────────────────────────────────────────────────────────────
  if (!showLanding) {
    const filters = activeTab === 'faceAR' ? AR_FILTERS : STYLE_FILTERS;
    const active  = activeTab === 'faceAR' ? currentAR : currentStyle;

    return (
      <div className="min-h-screen bg-[#080611] text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Navbar />

        <div className="max-w-[1120px] mx-auto px-6 py-8 pt-28">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-white text-xs mb-5 transition-colors tracking-wider uppercase">
            <ArrowLeft size={12} /> Back
          </Link>

          <div className="flex items-end gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>mbk studio</h1>
              <p className="text-[11px] text-gray-600 tracking-widest mt-0.5">a test trial</p>
            </div>
          </div>

          {/* Tab row */}
          <div className="flex gap-1.5 mb-5 p-1 bg-white/[0.03] border border-white/[0.06] rounded-xl w-fit">
            {[['faceAR','Face AR'],['style','Style FX']].map(([id,label]) => (
              <button key={id} onClick={() => setActiveTab(id)}
                className={`px-5 py-2 rounded-lg text-xs font-semibold tracking-wide transition-all ${
                  activeTab === id
                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-900/40'
                    : 'text-gray-500 hover:text-white'
                }`}>
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
            {/* Camera */}
            <div className="flex flex-col">
              {/* dev notice */}
              <div className="mb-2.5 flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-400/25 bg-amber-400/[0.05]">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                <p className="text-[11px] text-amber-200/85 tracking-wide">
                  still in development — improving latency. expect lag for now.
                </p>
              </div>
              <div className="relative rounded-2xl overflow-hidden bg-[#08060f]"
                style={{ aspectRatio: '4/3', boxShadow: '0 0 0 1px rgba(139,92,246,0.15), 0 24px 60px rgba(0,0,0,0.6)' }}>
                <video ref={videoRef} autoPlay playsInline muted className="hidden" />
                <canvas ref={canvasRef} className="w-full h-full object-cover" />

                {/* Live badge */}
                <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 text-[10px] flex items-center gap-2 tracking-widest uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Live
                </div>

                {/* Tracking badge */}
                <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 text-[10px] flex items-center gap-2 tracking-widest uppercase">
                  <span className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    !modelsReady ? 'bg-yellow-400 animate-pulse' :
                    faceDetected ? 'bg-violet-400 animate-pulse' : 'bg-gray-600'
                  }`} />
                  {trackingStatus}
                </div>

                {/* Active filter name overlay */}
                {active !== 'normal' && active !== 'none' && (
                  <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg px-3 py-1.5">
                    <span className="text-[10px] text-gray-400 tracking-widest uppercase">Filter · </span>
                    <span className="text-[10px] text-white font-semibold tracking-wider uppercase">{active}</span>
                  </div>
                )}
              </div>

              {/* Capture button */}
              <button onClick={captureImage}
                className="mt-3 w-full font-bold py-3.5 rounded-xl text-sm tracking-wide transition-all hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  fontFamily: 'Syne, sans-serif',
                  background: 'linear-gradient(135deg, #7c3aed, #a855f7 50%, #d946ef)',
                  boxShadow: '0 4px 24px rgba(124,58,237,0.45)',
                }}>
                magic
              </button>
            </div>

            {/* Right panel */}
            <div className="flex flex-col gap-4">
              {/* Filter grid */}
              <div className="bg-white/[0.025] border border-white/[0.07] rounded-2xl p-4">
                <p className="text-[10px] font-semibold tracking-widest text-gray-600 uppercase mb-3">
                  {activeTab === 'faceAR' ? 'AR Filters' : 'Style Effects'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {filters.map(filter => {
                    const isActive = active === filter.id;
                    return (
                      <button key={filter.id}
                        onClick={() => {
                          if (activeTab === 'faceAR') {
                            setCurrentAR(filter.id); currentARRef.current = filter.id;
                            setCurrentStyle('none'); currentStyleRef.current = 'none';
                            if (filter.id === 'normal') lastFaceRef.current = null;
                          } else {
                            setCurrentStyle(filter.id); currentStyleRef.current = filter.id;
                            setCurrentAR('normal'); currentARRef.current = 'normal';
                          }
                        }}
                        className={`relative p-3 rounded-xl text-left transition-all border ${
                          isActive
                            ? 'border-violet-500/60 bg-violet-500/10'
                            : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.1]'
                        }`}>
                        {isActive && (
                          <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-violet-400 shadow-[0_0_6px_rgba(168,85,247,0.8)]" />
                        )}
                        <span className={`inline-block text-[8px] font-bold px-1.5 py-0.5 rounded border tracking-widest mb-1.5 ${tagColor(filter.tag)}`}>
                          {filter.tag}
                        </span>
                        <div className="text-xs font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{filter.name}</div>
                        <div className="text-[10px] text-gray-600 mt-0.5">{filter.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Captures */}
              <div className="bg-white/[0.025] border border-white/[0.07] rounded-2xl p-4 flex-1">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold tracking-widest text-gray-600 uppercase">Captures</p>
                  <span className="text-[10px] font-bold text-violet-400 bg-violet-500/10 border border-violet-500/20 rounded-full px-2.5 py-0.5">
                    {captures.length}
                  </span>
                </div>
                <div className="flex flex-col gap-2 max-h-60 overflow-y-auto pr-0.5" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(139,92,246,0.3) transparent' }}>
                  {captures.length === 0 ? (
                    <div className="h-28 flex items-center justify-center text-[11px] text-gray-700 tracking-wider">
                      No captures yet
                    </div>
                  ) : captures.map(cap => (
                    <div key={cap.timestamp} className="rounded-xl overflow-hidden border border-white/[0.06] bg-black/20 group">
                      <img src={cap.url} alt="cap" className="w-full aspect-video object-cover cursor-pointer opacity-90 group-hover:opacity-100 transition-opacity"
                        onClick={() => window.open(cap.url, '_blank')} />
                      <div className="flex items-center justify-between px-3 py-2">
                        <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{cap.filter}</span>
                        <button onClick={() => downloadCapture(cap.url)}
                          className="text-[10px] font-bold px-3 py-1 rounded-lg bg-violet-600/80 hover:bg-violet-500 text-white transition-colors">
                          Save
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Landing ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#080611] flex items-center justify-center relative overflow-hidden">
      <Navbar />
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, rgba(124,58,237,0.1) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }} />
      <div className="absolute top-[42%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(124,58,237,0.2) 0%, transparent 65%)' }} />

      <div className="flex flex-col items-center z-10">
        <div className="relative mb-8 animate-[bob_4s_ease-in-out_infinite]">
          <style>{`@keyframes bob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }`}</style>
          <div className="relative">
            <div className="absolute -inset-2 rounded-full border border-purple-400/15"
              style={{ boxShadow: '0 0 50px rgba(168,85,247,0.2)' }} />
            <div className="w-56 h-56 rounded-full flex items-center justify-center"
              style={{
                background: 'radial-gradient(circle at 38% 32%, #1a0d3d, #060410)',
                border: '2px solid rgba(168,85,247,0.4)',
                boxShadow: '0 0 80px rgba(124,58,237,0.35), inset 0 0 60px rgba(124,58,237,0.06)',
              }}>
              <span className="text-purple-400 text-7xl opacity-35"
                style={{ fontFamily: 'Dancing Script, cursive', transform: 'rotate(-12deg)' }}>
                mbk
              </span>
            </div>
            <div className="absolute bottom-2 right-1 w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center text-white font-bold text-xs z-10"
              style={{ boxShadow: '0 0 20px rgba(124,58,237,0.9)' }}>
              AI
            </div>
          </div>
        </div>

        <h1 className="text-5xl font-extrabold text-white text-center mb-2"
          style={{ fontFamily: 'Syne, sans-serif', letterSpacing: '-1.5px' }}>
          mbk studio
        </h1>
        <p className="text-gray-600 mb-8 text-xs tracking-widest">a test trial</p>

        <button onClick={startStudio}
          className="font-bold px-12 py-4 rounded-full text-sm tracking-wide transition-all hover:scale-105 active:scale-95"
          style={{
            fontFamily: 'Syne, sans-serif',
            background: 'linear-gradient(135deg, #7c3aed, #a855f7 50%, #d946ef)',
            boxShadow: '0 0 32px rgba(124,58,237,0.5)',
          }}>
          Launch Studio
        </button>
      </div>
    </div>
  );
}
